<template>
  <appointment-schedule>
    <template v-slot:side-bar>
      <appointment-side-bar
        initial-category="cabinets"
        v-bind:appointment-near-list="appointmentNearList"
        v-bind:cabinet-list="cabinets"
        v-bind:initial-cabinets-ids="cabinetIds"
        v-bind:initial-date="day"
        v-bind:initial-period="period"
        v-bind:is-collapsed="isCollapsedSideBar"
        v-bind:nearest-unconfirmed-appointment-count="nearestUnconfirmedAppointmentCount"
        v-bind:orders-by-aggregators="ordersByAggregators"
        v-bind:orders-by-widgets="ordersByWidgets"
        v-bind:prev-category="prevCategory"
        v-bind:waiting-list-count="waitingList.length"
        v-on:collapse="onSidebarCollapsed"
        v-on:open-aggregator-orders="openAggregatorOrdersDialog"
        v-on:open-near-appointment="onOpenNearAppointment"
        v-on:open-nearest-unconfirmed-appointment-list="openNearestUnconfirmedAppointmentList"
        v-on:open-waiting-list="openWaitingList"
        v-on:open-free-doctor="toggleFreeDoctorControls"
        v-on:open-widget-orders="openWidgetOrdersDialog"
        v-on:open-work-time-controls="toggleDialogWorkTimeControls"
      />
    </template>
    <template v-slot:only-print>
      <h3>{{ viewDateTitle }}</h3>
    </template>
    <appointment-schedule-head
      v-bind:date-title="dateTitle"
      v-bind:initial-date="day"
      v-on:collapse="onSidebarCollapsed(true)"
      v-on:open-day="onOpenDay"
    />
    <div
      class="schedule-appointment__body"
      ref="scheduleAppointmentTable">
      <time-line
        v-if="!loadedCabinetListIsEmpty"
        v-bind:is-disable-export="isDisableExport"
        v-bind:rulerUnitCSS="$options.rulerUnitCSS"
        v-bind:timeline="timeline"
        v-bind:value="step"
        v-on:export-to-excel="exportToExcel"
        v-on:input="onInputStep"
        v-on:print-page="printPage"
      />
      <div
        class="schedule-appointment__timetable__grid"
        v-bind:class="{ 'h-auto': isEmptySheduleCabinets }"
      >
        <article
          class="schedule-appointment__timetable__column"
          v-for="day in timetableCabinetList"
          v-bind:key="day.date">
          <timetable-column-header
            v-bind:title="day.weekday"
            v-bind:subtitle="day.short_title"
            v-on:print-column="printPage(day.date)">
            <menu-cabinet-action
              v-bind:actions="actions"
              v-bind:cabinet-id="cabinetId"
              v-bind:day="day.date"
              v-on:open-cabinet="onOpenCabinet"
              v-on:set-day-off="onSetDayOff" />
          </timetable-column-header>
          <h3 class="schedule-appointment__timetable__notification" v-if="day.slots.length === 0">Рабочих часов нет</h3>
          <timetable-cell
            v-for="(slot, index) in day.slots"
            v-bind:key="index"
            v-bind:can-move-appointment="canMoveAppointment(day)"
            v-bind:cell="slot"
            v-bind:drag-appointment="dragAppointment"
            v-bind:expanded-slots="expandedSlots"
            v-bind:show-employee-name="true"
            v-bind:has-view-patient-invoices="hasViewPatientInvoices"
            v-bind:date="day.date"
            v-bind:messengers-access="messengersAccess"
            v-on:click-disabled-cell="onClickDisabledCell"
            v-on:create-invoice="onCreateInvoice"
            v-on:open-invoices="onOpenInvoices"
            v-on:drag-appointment-end="onDragAppointmentEnd"
            v-on:drag-appointment-start="onDragAppointmentStart"
            v-on:initiate-call="onInitiateCallByAppointment"
            v-on:move-appointment="onMoveAppointment(
              $event,
              day,
            )"
            v-on:open-appointment="openAppointmentDialog(
              $event.id,
              day.date,
              $event.employee,
              $event.ends_time,
              $event.starts_time,
              cabinetId,
            )"
            v-on:open-context-menu="onOpenContextMenu(day.date, $event)"
            v-on:open-waitings="openWaitingList" />
        </article>
        <template v-if="loadedCabinetListIsEmpty">
          <div
            class="schedule-appointment__notification">
            <button
              title="Открыть меню"
              class="schedule-appointment__collapse"
              type="button"
              v-on:click="onSidebarCollapsed(true)"
            />
            На {{ viewDate }} у кабинетов нет рабочих часов. Перейдите в раздел
            <router-link v-bind:to="{ name: 'ClinicPanelWorkSchedule' }">
              График работы</router-link>, чтобы добавить врачам рабочие дни.
          </div>
        </template>
      </div>
    </div>
    <iframe
      class="schedule-appointment__print"
      ref="printView"
      title="printView"
    />
    <context-menu-appointment-slot
      v-if="!disableContextMenu"
      v-bind:attach="contextMenuTarget"
      v-bind:rejection-reason-list="rejectionReasons"
      v-bind:isCopyAppointmentAction="isCopyAppointmentAction"
      v-bind:isPasteAppointmentAction="isPasteAppointmentAction"
      v-bind:disabledPasteAppointmentAction="disabledPasteAppointmentAction"
      v-bind:isChangeStatusAvailable="isChangeStatusAvailable"
      v-bind:isGoToPatientMedicalCard="isGoToPatientMedicalCard"
      v-bind:isPrintFutureAppointments="isPrintFutureAppointments"
      v-bind:isShowCancelModal="isShowCancelModal"
      v-bind:input-errors="changeStatusInputErrors"
      v-bind:current-status="currentStatus"
      v-bind:position-x="offsetContextMenuX"
      v-bind:position-y="offsetContextMenuY"
      v-model="isShowSlotContextMenu"
      v-on:copy="onCopyAppointment"
      v-on:paste="onPasteAppointmentCopy"
      v-on:change-appointment-status="changeAppointmentStatus"
      v-on:delete-rejection-reason="onDeleteRejectionReason"
      v-on:transfer-to-medcard="onPatientMedicalCard"
      v-on:print-future-appointments="onPrintFutureAppointments"
      v-on:show-cancel-dialog="onShowCancelDialog"
    />
    <modal-dialog
      v-model="needConfirmAppointmentPaste"
      title="При вставке записи в этот временной слот, продолжительность визита будет обрезана. Вставить?"
      with-cross
    >
      <div class="modal-dialog__buttons">
        <button
          class="modal-dialog__cancel"
          type="button"
          v-on:click="closeConfirmPasteDialog">Нет</button>
        <button
          class="modal-dialog__agree"
          type="button"
          v-on:click="onPasteAppointment(appointmentSlotForPaste)">Да</button>
      </div>
    </modal-dialog>
    <template v-slot:side-dialogs>
      <confirm-side-dialog
        v-if="wasSelectedCabinet"
        label="Редактирование кресла"
        v-bind:need-confirm="needConfirmCabinetForm"
        v-on:cancel="cabinet = null">
        <form-cabinet
          v-bind:cabinet="cabinet"
          v-bind:input-errors="cabinetInputErrors"
          v-on:close="cabinet = null"
          v-on:change-values="onChangeFieldValues"
          v-on:submit-editing="onCabinetEdit"
          ref="formCabinet" />
      </confirm-side-dialog>
      <confirm-side-dialog
        label="Свободные врачи"
        v-bind:size="'small'"
        v-else-if="shownFreeDoctorControlsDialog"
        v-on:cancel="closeFreeDoctorControlsDialog"
      >
        <time-step-minutes-picker
          v-on:change-time-step="onChangeTimeStepMinutes"
        />
        <employee-free-dates-controls-list
          v-bind:employees="employeeFreeDates"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-else-if="isShowAppointmentForm"
        v-bind:label="dialogAppointmentTitle"
        v-bind:need-confirm="needConfirmAppointmentForm"
        v-on:cancel="onCloseAppointmentForm"
      >
        <form-appointment-creation
          has-copy
          method="dialog"
          v-bind:is-selected-appointments="wasSelectedAppointment"
          v-bind:advertising-channel-list="advertisingChannels"
          v-bind:rejection-reason-list="rejectionReasons"
          v-bind:can-create-patients="canCreatePatients"
          v-bind:can-edit-initial="canEditInitial"
          v-bind:doctor-assistant-list="doctorAssistants"
          v-bind:employee-list="appointmentWorkEmployees"
          v-bind:employee-timeline="appointmentEmployeeTimeline"
          v-bind:employee-work-days="appointmentEmployeeWorkDays"
          v-bind:favorite-services="servicesWithFavorites.favorites"
          v-bind:initial-date="appointmentDate"
          v-bind:initial-employee-id="appointmentEmployeeId"
          v-bind:initial-month="appointmentMonth"
          v-bind:suggested-patient-id="appointmentPatientId"
          v-bind:initial-time-ends="appointmentTimeEnds"
          v-bind:initial-time-starts="appointmentTimeStarts"
          v-bind:input-errors="appointmentInputErrors"
          v-bind:is-await-request="isAppointmentAwaitRequest"
          v-bind:is-outdated="appointmentIsOutdated"
          v-bind:matched-patients="appointmentMatchedPatients"
          v-bind:presets-duration="$options.presetsDuration"
          v-bind:patient-list="patients"
          v-bind:service-list="servicesWithFavorites.all"
          v-bind="appointmentProps"
          v-bind:has-view-patient-medical-card="hasViewPatientMedicalCard"
          v-bind:has-view-patient-visit="hasViewPatientVisit"
          v-bind:has-view-patient-invoices="hasViewPatientInvoices"
          v-on:came="onCameAppointment"
          v-on:cancel="onCancelAppointment"
          v-on:change-values="onChangeFieldValues"
          v-on:clear-matched-patients="onClearMatchedPatients"
          v-on:close="onCloseAppointmentForm"
          v-on:confirm="onConfirmAppointment"
          v-on:copy="onCopy"
          v-on:create-advertising-channel="onCreateAdvertisingChannel"
          v-on:delete-rejection-reason="onDeleteRejectionReason"
          v-on:input-date="onInputAppointmentDate"
          v-on:input-employee="onInputAppointmentEmployee"
          v-on:input-month="onInputAppointmentMonth"
          v-on:input-times="onInputAppointmentTimes"
          v-on:match-patients="onMatchedPatients"
          v-on:open-visit="onOpenVisit"
          v-on:open-waitings="openWaitingList"
          v-on:reload-timeline="onReloadEmployeeDayTimeline"
          v-on:submit-creating="onSubmitAppointmentNewPatient"
          v-on:submit-editing="onSubmitAppointmentExistsPatient"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-else-if="waiting"
        v-on:cancel="closeWaitingForm"
        v-bind:need-confirm="needConfirmWaitingForm"
        v-bind:label="waitingListFormTitle">
        <form-add-to-waiting-list
          v-bind:has-create-patients="canCreatePatients"
          v-bind:has-view-patient-phone="hasViewPatientPhone"
          v-bind:input-errors="waitingListInputErrors"
          v-bind:is-await-waiting-request="isAwaitWaitingRequest"
          v-bind:is-loading-patient-list="isWaitingListLoadingPatientList"
          v-bind:patient-list="waitingListPatients"
          v-bind:service-list="servicesWithFavorites.all"
          v-bind:favorite-services="servicesWithFavorites.favorites"
          v-bind:waiting="waiting"
          v-on:cancel="closeWaitingForm"
          v-on:change-values="onChangeFieldValues"
          v-on:submit-exist="onSubmitWaitingExistPatient"
          v-on:submit-new="onSubmitWaitingNewPatient"
          v-on:update-patient-search-query="onSearchWaitingListPatients" />
      </confirm-side-dialog>
      <side-dialog
        label="Свободное время для записи"
        v-else-if="waitingOpenTimes.length"
        v-on:cancel="closeWaitingOpenTimes">
        <waiting-open-times v-bind:items="waitingOpenTimes" />
      </side-dialog>
      <confirm-side-dialog
        label="Лист ожидания"
        v-else-if="showWaitingList"
        v-on:cancel="closeWaitingList"
        v-bind:size="'small'"
      >
        <waiting-list
          v-bind:actions="waitingListActions"
          v-bind:list="selectedWaitings"
          v-on:create-waiting="onCreateWaiting"
          v-on:delete-waiting="onDeleteWaiting"
          v-on:initiate-call="onInitiateCallByPatient"
          v-on:view-waiting="onViewWaiting"
          v-on:make-appointment="onMakeAppointmentFromWaitingList"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        class="work-time-controls-side-dialog"
        label="Учет рабочего времени"
        v-if="shownWorkTimeControlsDialog"
        v-bind:size="'normal'"
        v-on:cancel="closeWorkTimeControlsDialog"
      >
        <employee-work-time-controls-list
          class="work-time-controls-side-dialog__body"
          v-bind:employees="employeesForWorkTimeControls"
          v-bind:message="messageForWorkTimeControls"
          v-on:employee-counter-start="startEmployeeWorkTimeCounter"
          v-on:employee-counter-stop="stopEmployeeWorkTimeCounter" />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-if="shownNearestUnconfirmedAppointmentList"
        class="nearest-unconfirmed-appointment-dialog"
        label="Обзвон пациентов"
        v-bind:size="'small'"
        v-on:cancel="closeNearestUnconfirmedAppointmentList"
      >
        <nearest-unconfirmed-appointment-list
          class="nearest-unconfirmed-appointment-dialog__body"
          v-bind:actions="nearestUnconfirmedAppointmentActions"
          v-bind:list="nearestUnconfirmedAppointments"
          v-on:close="closeNearestUnconfirmedAppointmentList"
          v-on:edit-appointment="openAppointmentDialog"
          v-on:confirm-appointment="onConfirmAppointment"
          v-on:initiate-call="onInitiateCallByPatient"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-if="shownAggregatorOrdersDialog"
        class="aggregator-order-dialog"
        v-bind:size="'small'"
        v-bind:label="aggregatorDialogLabel"
        v-on:cancel="closeAggregatorOrdersDialog"
      >
        <aggregator-order-list
          class="aggregator-order-dialog__body"
          v-bind:orders="aggregator.orders"
          v-bind:cancelable="aggregator.cancelable"
          v-on:aggregator-cancel-order="onCancelAppointmentOrderFromAggregator"
          v-on:aggregator-make-order="onMakeAppointmentFromAggregatorOrder"
        />
      </confirm-side-dialog>
      <confirm-side-dialog
        v-if="shownWidgetOrdersDialog"
        class="aggregator-order-dialog"
        size="small"
        v-bind:label="widgetDialogLabel"
        v-on:cancel="closeWidgetOrdersDialog"
      >
        <aggregator-order-list
          class="aggregator-order-dialog__body"
          v-bind:orders="appointmentBookingWidget.orders"
          v-bind:cancelable="appointmentBookingWidget.cancelable"
          v-on:aggregator-cancel-order="onCancelAppointmentOrderFromWidget"
          v-on:aggregator-make-order="onMakeAppointmentFromWidgetOrder"
        />
      </confirm-side-dialog>
    </template>
  </appointment-schedule>
</template>
<script>
import AggregatorOrderList from '@/components/AggregatorOrderList.vue';
import AppointmentSideBar from '@/components/AppointmentSideBar.vue';
import EmployeeWorkTimeControlsList from '@/components/EmployeeWorkTimeControlsList.vue';
import FormAppointmentCreation from '@/components/FormAppointmentCreation.vue';
import SideDialog from '@/components/SideDialog.vue';
import TimeLine from '@/components/TimeLine.vue';
import dateMixin from '@/mixins/dateMixin';
import notifierMixin from '@/mixins/notifierMixin';
import { formatDate } from '@/utils/timeFormat';
import appointmentMixin, { AppointmentSlot, calcDifferenceMinutesForStringTime } from '@/mixins/appointmentMixin';
import TimetableCell from '@/components/TimetableCell.vue';
import MenuCabinetAction from '@/components/MenuCabinetAction.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import NearestUnconfirmedAppointmentList from '@/components/NearestUnconfirmedAppointmentList.vue';
import TimetableColumnHeader from '@/components/TimetableColumnHeader.vue';
import FormAddToWaitingList from '@/components/FormAddToWaitingList.vue';
import { MoveAppointmentRequest } from '@/storage/gateways/AppointmentScheduleGateway';
import FormCabinet from '@/components/FormCabinet.vue';
import cabinetViewMixin from '@/mixins/cabinetViewMixin';
import ContextMenuAppointmentSlot from '@/components/ContextMenuAppointmentSlot.vue';
import WaitingList from '@/components/WaitingList.vue';
import WaitingOpenTimes from '@/components/WaitingOpenTimes.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import AppointmentSchedule from '@/views/ClinicPanel/AppointmentSchedule.vue';
import AppointmentScheduleHead from '@/components/AppointmentScheduleHead.vue';
import { CLEAR_APPOINTMENT_COPY } from '@/store/actions/appointments';
import printMixin from '@/mixins/printMixin';
import EmployeeFreeDatesControlsList from '@/components/EmployeeFreeDatesControlsList.vue';
import TimeStepMinutesPicker from '@/components/TimeStepMinutesPicker.vue';

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

const AppointmentForm = FormAppointmentCreation.name;
const CabinetForm = FormCabinet.name;
const WaitingForm = FormAddToWaitingList.name;

const needConfirm = (form) => ({ needConfirmBeforeClosed }) => needConfirmBeforeClosed[form];

/**
 * @description Компонент страницы графика приемов.
 * @type {Vue}
 */
export default {
  components: {
    TimeStepMinutesPicker,
    EmployeeFreeDatesControlsList,
    AggregatorOrderList,
    AppointmentSchedule,
    AppointmentScheduleHead,
    AppointmentSideBar,
    ConfirmSideDialog,
    ContextMenuAppointmentSlot,
    EmployeeWorkTimeControlsList,
    FormAddToWaitingList,
    FormAppointmentCreation,
    FormCabinet,
    MenuCabinetAction,
    ModalDialog,
    NearestUnconfirmedAppointmentList,
    SideDialog,
    TimeLine,
    TimetableCell,
    TimetableColumnHeader,
    WaitingList,
    WaitingOpenTimes,
  },
  computed: {
    /**
     * @description Загруженный список кабинетов пуст.
     * @returns {boolean}
     */
    loadedCabinetListIsEmpty() {
      return !this.isAwaitReloadTimetable && !this.timetableCabinetList.length;
    },
    /**
     * @description Идентификатор кабинета.
     * @returns {number}
     */
    cabinetId: ({ $route }) => parseInt($route.params.id, 10),
    /**
     * @description Идентификатор кабинета.
     * @returns {number[]}
     */
    cabinetIds: ({ cabinetId }) => [cabinetId],
    /**
     * @description Деактивировать кнопку экспорта в excel.
     * @returns {boolean}
     */
    isDisableExport() {
      if (!this.actions.includes('export-excel')) {
        return false;
      }
      if (this.isAwaitReloadTimetable) {
        return true;
      }
      return !this.timetableCabinetList.some((item) => item.slots.length > 0);
    },
    /**
     * @description Выбранная дата в ISO формате.
     * @returns {string}
     */
    isoDate: ({ day, formatDateToISO }) => formatDateToISO(day),
    /**
     * @description Запрос данных расписания.
     * @returns {any}
     */
    viewQuery() {
      return {
        category: this.category,
        date: this.isoDate,
        period: this.period,
        step: this.step,
        value: this.cabinetId,
      };
    },
    /**
     * @description Проверка на кабинеты с пустым расписанием.
     * @returns {boolean}
     */
    isEmptySheduleCabinets() {
      return this.timetableCabinetList.every((cabinet) => !cabinet.slots.length);
    },
    viewDate: ({ isoDate }) => formatDate(new Date(isoDate)),
    viewDateTitle: ({ viewDate }) => `Расписание на ${viewDate}`,
    needConfirmAppointmentForm: needConfirm(AppointmentForm),
    needConfirmCabinetForm: needConfirm(CabinetForm),
    needConfirmWaitingForm: needConfirm(WaitingForm),
  },
  created() {
    this.reloadTimetable(this.viewQuery);
    this.fetchWaitingList();
  },
  data() {
    const needConfirmBeforeClosed = {};
    needConfirmBeforeClosed[AppointmentForm] = false;
    needConfirmBeforeClosed[CabinetForm] = false;
    needConfirmBeforeClosed[WaitingForm] = false;
    return {
      breakTimeline: [],
      /**
       * @description Список кабинетов.
       * @type {({color: string, id: string, name: string}[])}
       */
      cabinets: this.initialCabinets,
      dateTitle: '',
      /**
       * @description Перемещаемая запись на прием.
       * @type {any}
       */
      dragAppointment: {
        employeeId: 0,
        id: 0,
        ratioToStep: 1,
      },
      timetableCabinetList: [],
      /**
       * @description Необходимо подтверждение перед закрытием формы.
       * @type {any}
       */
      needConfirmBeforeClosed,
    };
  },
  methods: {
    /**
     * @description Проверить возможность перемещения записи на прием.
     * @param {any} day День.
     * @returns {function}
     */
    canMoveAppointment(day) {
      return (slotStart, slotSection = 0) => {
        const index = day.slots.findIndex((slot) => slot === slotStart);
        let size = index + this.dragAppointment.ratioToStep;
        for (let i = index; i < size; i += 1) {
          if (i >= day.slots.length) {
            return false;
          }
          const slot = day.slots[i];
          const isSelf = slot.id === this.dragAppointment.id;
          if (isSelf) {
            size = i + slotSection + 1;
          } else if (slot.tags.indexOf('add') === -1) {
            return false;
          }
        }
        return true;
      };
    },
    /**
     * @description Обработать перемещение записи на прием.
     * @param {string} time Новое время начала записи на прием.
     * @param {string} day  День.
     * @returns {void}
     */
    onMoveAppointment(time, day) {
      const startsAt = `${day.date} ${time}`;
      const request = new MoveAppointmentRequest(
        this.dragAppointment.id,
        this.dragAppointment.employeeId,
        startsAt,
      );
      this.moveAppointment(request);
    },
    /**
     * @description Получить список кабинетов.
     * @returns {({color: string, id: string, name: string}[])}
     */
    fetchCabinetList() {
      this.$cabinetGateway.getAll().then((result) => {
        result.forEach(({ cabinets }) => {
          this.cabinets = cabinets;
        });
      });
      return this.initialCabinets;
    },
    /**
     * @description Открыть кабинет.
     * @param {number} cabinetId Идентификатор кабинета.
     * @returns {void}
     */
    onOpenCabinet(cabinetId) {
      this.fetchCabinet(cabinetId);
    },
    /**
     * @description Обработать показ контекстного меню.
     * @param {number} isoDate Дата в формате ISO.
     * @param {{
     *  offset:any,
     *  currentStatus:string,
     *  target:Element,
     *  slot:any
     * }}              event Событие.
     */
    onOpenContextMenu(isoDate, {
      offset,
      currentStatus,
      slot,
      target,
    }) {
      const appointmentSlot = new AppointmentSlot(
        slot.actions,
        slot.tags,
        isoDate,
        slot.starts_time,
        slot.ends_time,
        slot.id,
        slot.employee ? slot.employee.id : null,
        this.cabinetId,
        slot.patient ? slot.patient.id : null,
        slot.has_next_appointments,
      );
      if (appointmentSlot.isOpenSlot() || appointmentSlot.isPatientSlot()) {
        this.openAppointmentSlotContextMenu(offset, currentStatus, appointmentSlot, target);
      }
    },
    /**
     * @description Открыть форму создания записи со скопированным пациентом.
     * @param {Object}          patient
     * @param {AppointmentSlot} slot
     * @returns {void}
     */
    openFormAppointmentCreationWithCopiedPatient(patient, slot) {
      const appointmentProps = {
        needsCreatingPatient: false,
        patientId: patient.patientId,
      };
      this.makeAppointmentFromCopy(appointmentProps, slot);
      this.$store.dispatch(CLEAR_APPOINTMENT_COPY);
    },
    /**
     * @description Обработать вставку скопированной записи на прием.
     * @returns {void}
     */
    onPasteAppointmentCopy() {
      this.appointmentSlotTarget.forEach(async (slot) => {
        if (this.appointmentCopy.source === 'patient') {
          this.openFormAppointmentCreationWithCopiedPatient(
            this.appointmentCopy,
            slot,
          );
          return;
        }
        const cabinetId = slot.getCabinetId();
        const isoDate = slot.getIsoDate();
        const { employeeId } = this.appointmentCopy;
        const isWork = await this.checkEmployeeIsWorkInCabinet(cabinetId, employeeId, isoDate);
        const startsTime = slot.getStartsTime();
        const targetTimetable = this.timetableCabinetList
          .find((timetable) => timetable.date === isoDate);
        const slots = targetTimetable
          .slots
          .filter((timetableSlot) => timetableSlot.starts_time >= startsTime);
        const targetDuration = this.appointmentCopy.intervalDurationInMinutes;
        const endsTimeDuration = this.prepareEndsTimeDurationForPasteAppointment(
          slots,
          startsTime,
          targetDuration,
        );
        const calculatedSlot = slot.copyWithProps(
          {
            employeeId: isWork ? employeeId : -1,
            endsTime: this.getEndsTimeByStartAndDuration(startsTime, endsTimeDuration),
          },
        );
        if (isWork) {
          if (endsTimeDuration !== targetDuration) {
            this.needConfirmAppointmentPaste = true;
            this.appointmentSlotForPaste = calculatedSlot;
            return;
          }
          this.onPasteAppointment(calculatedSlot);
        } else {
          this.notifyFail('У сотрудника нет рабочих часов в этом кресле');
          this.makeAppointmentFromCopy(this.appointmentCopy, calculatedSlot);
        }
      });
    },
    /**
     * @description Обработать редактирование кабинета.
     * @param {CabinetEditingRequest} request Запрос.
     * @returns {void}
     */
    onCabinetEdit(request) {
      const onResponse = (result) => {
        result.forEach((response) => {
          this.notifySuccess(response.message);
          this.cabinet = null;
          this.reloadTimetable(this.viewQuery);
          this.fetchCabinetList();
        });
      };
      this.sendEditCabinetRequest(request).then(onResponse);
    },
    /**
     * @description Скопировать запись на прием.
     * @returns {void}
     */
    onCopyAppointment() {
      this.appointmentSlotTarget.forEach((slot) => {
        const timeIntervalInMinutes = calcDifferenceMinutesForStringTime(
          slot.getStartsTime(),
          slot.getEndsTime(),
        );
        const appointmentId = slot.getAppointmentId();
        const employeeId = slot.getEmployeeId();
        this.copyAppointmentById(appointmentId, employeeId, timeIntervalInMinutes);
      });
    },
    /**
     * @description Установить день нерабочим.
     * @returns {void}
     */
    onSetDayOff(cabinetId, date) {
      const onLeft = ({ title }) => {
        this.notifyFail(title);
      };
      const onRight = ({ message }) => {
        this.reloadTimetable(this.viewQuery);
        if (message) {
          this.notifySuccess(message);
        }
      };
      this.$workScheduleGateway
        .setCabinetDayOff(cabinetId, date)
        .then((result) => result.fold(onLeft, onRight));
    },
    /**
     * @description Перезагрузить данные таблицы.
     * @param {string} date Дата.
     * @param {number} step Шаг.
     * @returns {void}
     */
    async reloadTimetable({ date, step }) {
      this.fetchNearestUnconfirmedAppointments();
      this.fetchAppointmentOrders();
      this.fetchExpandedAppointments(
        this.cabinetId,
        date,
        this.period,
      );
      this.isAwaitReloadTimetable = true;
      try {
        this.onCloseAppointmentForm();
        const response = await this.$appointmentScheduleGateway.getViewByCabinetForRange(
          this.cabinetId,
          date,
          step,
          this.period,
        );
        response.forEach((result) => {
          this.actions = result.actions || [];
          this.appointmentNearList = result.near_appointments;
          this.cabinets = result.all_cabinets;
          this.dateTitle = result.title;
          this.step = result.step_minutes;
          this.timeline = result.timeline;
          this.timetableCabinetList = result.days;
        });
      } finally {
        this.isAwaitReloadTimetable = false;
      }
    },
    /**
     * @description Получить расширенные записи на прием.
     * @param {number} cabinetId Идентификатор кабинета.
     * @param {string} date      Дата в формате ISO.
     * @param {string} period    Период.
     * @returns {void}
     */
    fetchExpandedAppointments(cabinetId, date, period) {
      const onResponse = (response) => {
        response.forEach((result) => { this.expandedSlots = result; });
      };
      this
        .$appointmentScheduleGateway
        .getExpandedViewByCabinetForRange(cabinetId, date, period)
        .then(onResponse);
    },
    /**
     * @description Печать расписания.
     * @param {string} date Дата.
     * @returns {void}
     */
    printPage(date = '') {
      const recoverList = [];
      if (date) {
        recoverList.push(...this.timetableCabinetList);
        this.timetableCabinetList = this.timetableCabinetList.filter((item) => item.date === date);
      }
      this.$nextTick(() => {
        this.hideWhenPrintElement();
        const rect = this.$refs.scheduleAppointmentTable.getBoundingClientRect();
        const width = rect.width + 20;
        const height = rect.height + 70;
        const orientation = rect.width > rect.height ? 'landscape' : 'portrait';
        const css = `@media print { @page { size: ${orientation}; size: ${width}px ${height}px; } }`;
        const head = document.head || document.getElementsByTagName('head')[0];
        const style = document.createElement('style');
        head.appendChild(style);
        if (style.styleSheet) {
          style.styleSheet.cssText = css;
        } else {
          style.appendChild(document.createTextNode(css));
        }
        window.print();
        this.showNotWhenPrintElement();
        head.removeChild(style);
        if (recoverList.length > 0) {
          this.timetableCabinetList = recoverList;
        }
      });
    },
    /**
     * @description Экспортировать расписание приемов в эксель.
     * @returns {void}
     */
    async exportToExcel() {
      const request = await this.$appointmentScheduleGateway.getSheetViewCabinetByRange(
        this.cabinetId,
        this.isoDate,
        this.step,
        this.period,
      );
      request.forEach((file) => {
        saveFile(file, 'Расписание.xlsx');
      });
    },
  },
  mixins: [
    appointmentMixin,
    cabinetViewMixin,
    dateMixin,
    notifierMixin,
    printMixin,
  ],
  name: 'ClinicPanelAppointmentScheduleCabinetPeriod',
  props: {
    initialCabinets: {
      default: () => [],
      type: Array,
    },
  },
};
</script>
<style lang="scss" scoped>
.schedule-appointment {
  &__print {
    display: none;
  }
  &__timetable {
    &__empty {
      width: 173px;
      border-top: none;
    }
    &__notification {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 53px;
    }
  }
}
</style>
